/**
 * Web チェックイン UI（フェーズ3・要件10-3〜10-6）。
 *
 * ログイン中のみ表示。チェックインボタン → モーダルで操作する。
 * - 「今ここ」: ブラウザ GPS → POST /api/checkin（source=web-gps）
 * - GPS 拒否/失敗 → 会場プリセット選択へ誘導（source=web-preset）
 * - 公開トグル: visible / trail_visible を POST /api/visibility で更新
 */
import { useState } from 'react';
import { getMyProfile, postCheckin, updateVisibility } from '../api/client';
import { loadTokens } from './cognito';
import type { UseAuthResult } from './useAuth';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { VENUES } from '../map/venues';
import { useMenuModal } from './userMenuContext';
import './checkinControl.css';

export interface CheckinControlProps {
  /** App で一度だけ呼んだ useAuth の結果を渡す。 */
  readonly auth: UseAuthResult;
  readonly lang: Lang;
  /**
   * トリガーボタンの表示形態。
   * - 'menu'（既定）: ユーザーメニュー内の項目として表示（絶対配置しない）
   * - 'floating': 旧来の左下フロート配置（後方互換・現状未使用）
   */
  readonly variant?: 'menu' | 'floating';
}

export function CheckinControl({
  auth,
  lang,
  variant = 'menu',
}: CheckinControlProps) {
  // ログイン状態は useAuth（App で1回実行）に連動。確定後に再レンダーされて出る。
  const { enabled, user, loading } = auth;
  const loggedIn = enabled && !loading && !!user;
  const m = messages(lang);

  const [open, setOpen] = useMenuModal();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showVenues, setShowVenues] = useState(false);
  const [visible, setVisible] = useState(true);
  const [trailVisible, setTrailVisible] = useState(true);
  /**
   * 公開トグルの保存値の読み込み状態（タスク43）。開くたびに GET /api/profile で取得し、
   * 取得できるまではトグルを操作させない（保存値と違う「表示」を見せて誤チェックインさせないため）。
   */
  const [settings, setSettings] = useState<'loading' | 'ready' | 'failed'>('loading');

  function idToken(): string | null {
    return loadTokens()?.id_token ?? null;
  }

  async function openModal(): Promise<void> {
    setOpen(true);
    setMsg(null);
    setShowVenues(false);
    setSettings('loading');
    const token = idToken();
    if (!token) {
      setSettings('failed');
      return;
    }
    try {
      const profile = await getMyProfile(token);
      setVisible(profile.visible);
      setTrailVisible(profile.trail_visible);
      setSettings('ready');
    } catch (err) {
      console.error('visibility settings load failed', err);
      setSettings('failed');
    }
  }

  async function checkinAt(
    lat: number,
    lng: number,
    source: 'web-gps' | 'web-preset',
  ): Promise<void> {
    const token = idToken();
    if (!token) {
      setMsg(m.checkin.sessionExpired);
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await postCheckin(token, { lat, lng, source });
      // 非公開のままチェックインした場合は、地図に出ないことをはっきり伝える（タスク43）
      setMsg(visible ? m.checkin.done : m.checkin.doneHidden);
      setShowVenues(false);
      // 完了後、少し見せてから自動で閉じる
      setTimeout(() => setOpen(false), 1500);
    } catch {
      setMsg(m.checkin.failed);
    } finally {
      setBusy(false);
    }
  }

  function handleHere(): void {
    if (!navigator.geolocation) {
      setMsg(m.checkin.noGeo);
      setShowVenues(true);
      return;
    }
    setBusy(true);
    setMsg(m.checkin.locating);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void checkinAt(pos.coords.latitude, pos.coords.longitude, 'web-gps');
      },
      (err) => {
        setBusy(false);
        // 失敗理由で案内を出し分ける（デバッグ性と UX のため）。
        // 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        let reason: string;
        if (err.code === err.PERMISSION_DENIED) {
          reason = m.checkin.denied;
        } else if (err.code === err.TIMEOUT) {
          reason = m.checkin.timeout;
        } else {
          reason = m.checkin.geoFailed;
        }
        console.warn('checkin geolocation failed', err.code, err.message);
        setMsg(reason);
        setShowVenues(true);
      },
      // 会場での合流用途に高精度測位は不要。高精度はタイムアウトしやすいため false にし、
      // 直近1分のキャッシュ位置（地図の現在地表示で既に取得済みの座標）も許容して成功率を上げる。
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
    );
  }

  async function toggleVisible(next: boolean): Promise<void> {
    const token = idToken();
    if (!token) return;
    setVisible(next);
    try {
      await updateVisibility(token, { visible: next });
    } catch {
      setVisible(!next);
    }
  }

  async function toggleTrail(next: boolean): Promise<void> {
    const token = idToken();
    if (!token) return;
    setTrailVisible(next);
    try {
      await updateVisibility(token, { trail_visible: next });
    } catch {
      setTrailVisible(!next);
    }
  }

  if (!loggedIn) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={variant === 'menu' ? 'user-menu__item' : 'checkin-trigger'}
        onClick={() => void openModal()}
      >
        {m.checkin.trigger}
      </button>

      {open && (
        <div className="checkin-modal__overlay" onClick={() => setOpen(false)}>
          <div
            className="checkin-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={m.checkin.title}
          >
            <h2 className="checkin-modal__title">{m.checkin.title}</h2>

            <button
              type="button"
              className="checkin-modal__here"
              onClick={handleHere}
              disabled={busy || settings === 'loading'}
            >
              {m.checkin.here}
            </button>

            <button
              type="button"
              className="checkin-modal__venue-toggle"
              onClick={() => setShowVenues((v) => !v)}
              disabled={busy || settings === 'loading'}
            >
              {m.checkin.fromVenue}
            </button>

            {showVenues && (
              <ul className="checkin-modal__venues">
                {VENUES.map((v) => (
                  <li key={v.id}>
                    <button
                      type="button"
                      onClick={() =>
                        void checkinAt(
                          v.coordinates[1],
                          v.coordinates[0],
                          'web-preset',
                        )
                      }
                      disabled={busy}
                    >
                      {v.emoji} {lang === 'ja' ? v.name : v.nameEn}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="checkin-modal__toggles">
              <label>
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={(e) => void toggleVisible(e.target.checked)}
                  disabled={settings !== 'ready'}
                />
                {m.checkin.showOnMap}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={trailVisible}
                  onChange={(e) => void toggleTrail(e.target.checked)}
                  // 軌跡はピンに従属する（visible=false ならサーバーはユーザーごと返さない）。
                  // 「地図に表示する」がオフの間は押せなくし、軌跡の設定値はそのまま残す（タスク44）。
                  disabled={settings !== 'ready' || !visible}
                />
                {m.checkin.showTrail}
              </label>
              {settings === 'ready' && !visible && (
                <p className="checkin-modal__hint">{m.checkin.trailNeedsMap}</p>
              )}
            </div>
            {settings === 'loading' && (
              <p className="checkin-modal__msg">{m.checkin.loadingSettings}</p>
            )}
            {settings === 'failed' && (
              <p className="checkin-modal__msg">{m.checkin.settingsFailed}</p>
            )}

            {msg && <p className="checkin-modal__msg">{msg}</p>}

            <button
              type="button"
              className="checkin-modal__close"
              onClick={() => setOpen(false)}
            >
              {m.common.close}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
